import { Box, Table, TableBody, TableContainer, useTheme } from "@mui/material";

import BottomScrollListener from "~src/components/BottomScrollListener";

import { useEffect, useMemo } from "react";
import { BrandDiscoverOrdering } from "~src/api/discover";
import ContentBox from "~src/components/ContentBox";
import EmptyContent from "~src/components/EmptyContent";
import { TableHeadCustom, TableSkeleton } from "~src/components/table";
import useTable from "~src/hooks/useTable";
import { PATH_BRANDS } from "~src/routes/paths";
import BrandRow from "./BrandRow";
import { generateTableHeader } from "./TableHeaders";
import { DiscoverBrandContext } from "./useInfiniteDiscoverBrands";
import { useNavigate } from "react-router-dom";

export const DiscoverBrandsList = ({
  isPreFilteredMode,
  discoverBrandsContext,
  columns,
  onOrderingUpdated,
}: {
  isPreFilteredMode?: boolean;
  discoverBrandsContext: DiscoverBrandContext;
  columns: string[];
  onOrderingUpdated?: (sort: BrandDiscoverOrdering) => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data, loading, loadMore } = discoverBrandsContext;
  const { dense, order, orderBy, onSort } = useTable();

  const tableHeaders = useMemo(() => {
    return generateTableHeader(columns);
  }, [columns]);

  useEffect(() => {
    let ordering = orderBy as BrandDiscoverOrdering;
    if (order === "desc") {
      ordering = `-${orderBy}` as BrandDiscoverOrdering;
    }
    if (onOrderingUpdated) {
      onOrderingUpdated(ordering);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy]);

  if ((!data || data.length === 0) && !loading) {
    return (
      <ContentBox data-testid="discover-brands-list">
        <EmptyContent
          title="No brands"
          description="Try clearing your filters or using another search term."
        />
      </ContentBox>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: "12px",
        contentOverflow: "hidden",
      }}
      data-testid="discover-brands-list"
    >
      <TableContainer
        sx={{
          position: "relative",
        }}
      >
        <Table size={dense ? "small" : "medium"}>
          {data?.length > 0 && (
            <TableHeadCustom
              headLabel={tableHeaders}
              rowCount={0}
              order={order}
              orderBy={orderBy}
              onSort={onSort}
            />
          )}

          <TableBody>
            {data?.map((row, index) => {
              return (
                <BrandRow
                  key={`${row.brand_id}-${index}`}
                  index={index}
                  row={row}
                  columns={columns}
                  onSelectRow={() => {
                    navigate(PATH_BRANDS.overview(row.brand_id), {
                      state: { source: 'discover_brands', brandData: row }
                    });
                  }}
                  isPreFilteredMode={isPreFilteredMode}
                />
              );
            })}
            {loading && (
              <>
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <BottomScrollListener
        onBottom={() => {
          loadMore();
        }}
        //offset={2000}
      />
    </Box>
  );
};
